import { FilterModel } from '@ag-grid-community/core/dist/esm/es6/interfaces/iFilter';
import { IServerSideGetRowsParams } from '@ag-grid-community/core/dist/esm/es6/interfaces/iServerSideDatasource';
import { Injectable } from '@angular/core';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { exhaustMap, map, Observable } from 'rxjs';
import { CurrentShijiUserInterface } from 'shared/interfaces/current-shiji-user.interface';
import { CurrentSupplierUserInterface } from 'shared/interfaces/current-supplier-user.interface';
import { GridService } from 'shared/interfaces/grid-service';
import { CachingService, EventBusService } from 'shared/services';
import { UserApiService } from 'shared/services/userApi.service';
import { ListingsService } from './listings.service';

@Injectable({
    providedIn: 'root',
})
export class ListingsGridService implements GridService {
    private apollo: ApolloBase;

    constructor(
        private apolloProvider: Apollo,
        private userApiService: UserApiService,
        private cachingService: CachingService,
        private eventBusService: EventBusService,
        private listingsService: ListingsService,
    ) {
        this.apollo = this.apolloProvider.use('listingAPI');
    }

    NAME = 'ListingsGridService';

    public getRows(params: IServerSideGetRowsParams, pageNumber: number, pageSize: number): Observable<any> {
        return this.userApiService.getLoginInformation().pipe(
            exhaustMap((userData: CurrentSupplierUserInterface | CurrentShijiUserInterface) => {
                const supplierId = (userData as CurrentSupplierUserInterface)?.supplierId;

                const variables: any = {
                    orderBy: params.request.sortModel[0]?.colId,
                    orderDirection: params.request.sortModel[0]?.sort?.toUpperCase() ?? 'ASC',
                    companyName: (params.request.filterModel as FilterModel)['companyName']?.filter,
                    name: (params.request.filterModel as FilterModel)['name']?.filter,
                    city: (params.request.filterModel as FilterModel)['city']?.filter,
                    countryName: (params.request.filterModel as FilterModel)['countryName']?.filter,
                    brandName: (params.request.filterModel as FilterModel)['brandName']?.filter,
                    shijiId: (params.request.filterModel as FilterModel)['shijiId']?.filter,
                    mappedId: (params.request.filterModel as FilterModel)['mappedId']?.filter,
                    listingStatuses: (params.request.filterModel as FilterModel)['listingStatus']?.values,
                };

                return this.listingsService.searchListings(variables, pageNumber, pageSize, supplierId);
            }),
        );
    }

    public getFilteredListingsForAccessGroups(
        listingNameFilter: string = '',
        supplierId: string,
        companyNameFilter: string,
    ): Observable<any> {
        const query = `query getListings {
            listings {
                searchListings(searchQuery: {
                        pageNumber: 1
                        pageSize: 10000
                        orderBy: "name"
                        orderDirection: ASC
                    },
                    filtering: {
                        ${companyNameFilter ? 'companyName: "' + companyNameFilter + '"' : ''},
                        ${listingNameFilter ? 'name: "' + listingNameFilter + '"' : ''}
                }) {
                    data {
                        id
                        name
                    }
                    totalCount
                }
            }
        }`;

        return this.apollo
            .query({
                query: gql(query),
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.listings.searchListings.data;
                }),
            );
    }
}
